import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useRaynetConnection } from '@/hooks/useRaynet';
import { adminFormApi } from '@/api/raynetClient';
import { CheckCircle, AlertCircle, Activity, Clock, TrendingUp, Users, Eye, ChevronLeft, ChevronRight, Search, Building2, User, Calendar, Edit3, ExternalLink, Trash2 } from 'lucide-react';

interface FormStats {
  statusBreakdown: Record<string, number>;
  totalForms: number;
  recentActivity: Array<{
    uuid: string;
    company_name: string;
    representative: string;
    status: string;
    created_at: string;
    updated_at: string;
    submitted_at?: string;
  }>;
  representativeStats: Array<{
    representative: string;
    total_forms: number;
    completed_forms: number;
    last_activity: string;
  }>;
}

interface FormData {
  uuid: string;
  company_name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  status: string;
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  visit_date?: string;
  representative?: string;
  completion_percentage: number;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export function MonitoringTab() {
  const { data: isConnected } = useRaynetConnection();
  const [stats, setStats] = useState<FormStats | null>(null);
  const [forms, setForms] = useState<FormData[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAllForms, setShowAllForms] = useState(false);
  
  // Bulk selection state
  const [selectedForms, setSelectedForms] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    status: '',
    representative: '',
    company_name: '',
  });

  // Load statistics
  const loadStats = async () => {
    try {
      const result = await adminFormApi.getStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('❌ Error loading admin stats:', error);
    }
  };

  // Load forms with pagination
  const loadForms = async () => {
    setIsLoading(true);
    try {
      const result = await adminFormApi.getForms({
        ...filters,
        // Only apply text filters if they're not empty
        status: filters.status || undefined,
        representative: filters.representative || undefined,
        company_name: filters.company_name || undefined,
      });
      
      if (result.success) {
        setForms(result.data);
        setPagination(result.pagination);
      }
    } catch (error) {
      console.error('❌ Error loading admin forms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (showAllForms) {
      loadForms();
    }
  }, [showAllForms, filters]);

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 })); // Reset to page 1 when filtering
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return (
          <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
            Rozpracováno
          </Badge>
        );
      case 'submitted':
        return (
          <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
            Odesláno
          </Badge>
        );
      case 'synced':
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
            Synchronizováno
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
            Chyba
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-gray-100 text-gray-800">
            {status}
          </Badge>
        );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };


  // Handle opening form for editing
  const handleOpenForm = (formUuid: string) => {
    // Open form in new tab for admin editing
    const url = `/form?formId=${formUuid}&adminMode=true`;
    window.open(url, '_blank');
  };

  // Handle continuing form in same tab
  const handleContinueForm = (formUuid: string) => {
    // Navigate to form in same tab
    window.location.href = `/form?formId=${formUuid}&adminMode=true`;
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectedForms.size === forms.length) {
      setSelectedForms(new Set());
    } else {
      setSelectedForms(new Set(forms.map(form => form.uuid)));
    }
  };

  const handleSelectForm = (formUuid: string) => {
    const newSelected = new Set(selectedForms);
    if (newSelected.has(formUuid)) {
      newSelected.delete(formUuid);
    } else {
      newSelected.add(formUuid);
    }
    setSelectedForms(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedForms.size === 0) return;
    
    const confirmed = window.confirm(
      `Opravdu chcete smazat ${selectedForms.size} formulářů? Tato akce je nevratná.`
    );
    
    if (!confirmed) return;
    
    setIsDeleting(true);
    try {
      await adminFormApi.bulkDeleteForms(Array.from(selectedForms));
      setSelectedForms(new Set());
      // Reload data
      await loadForms();
      await loadStats();
    } catch (error) {
      console.error('❌ Error bulk deleting forms:', error);
      alert('Chyba při mazání formulářů: ' + (error instanceof Error ? error.message : 'Neznámá chyba'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* API Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Status API
          </CardTitle>
          <CardDescription>
            Aktuální stav připojení k Raynet API
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className={isConnected ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            {isConnected ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-600" />
            )}
            <AlertDescription className={isConnected ? 'text-green-800' : 'text-red-800'}>
              {isConnected 
                ? 'Raynet API je dostupné a funkční'
                : 'Raynet API není dostupné - aktivován offline režim'
              }
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Celkem formulářů
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalForms}</div>
              <p className="text-xs text-muted-foreground">
                všech časů
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Rozpracováno
              </CardTitle>
              <Clock className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {stats.statusBreakdown.draft || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                vyžadují dokončení
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Odesláno
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.statusBreakdown.submitted || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                synchronizováno do Raynetu
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Nedávná aktivita
              </CardTitle>
              <CardDescription>
                {showAllForms ? 'Všechny formuláře s filtrováním' : 'Posledních 10 aktivit'}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowAllForms(!showAllForms)}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              {showAllForms ? 'Skrýt vše' : 'Zobrazit vše'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!showAllForms ? (
            // Recent activity view
            <div className="space-y-4">
              {stats?.recentActivity.map((activity) => (
                <div key={activity.uuid} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                  <div className="flex items-center gap-3">
                    {activity.status === 'synced' ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : activity.status === 'error' ? (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    ) : (
                      <Clock className="h-4 w-4 text-orange-600" />
                    )}
                    <div>
                      <p className="font-medium">
                        {activity.company_name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {activity.representative} • {formatDate(activity.updated_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(activity.status)}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenForm(activity.uuid)}
                      className="text-orange-600 hover:text-orange-700"
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {!stats?.recentActivity.length && (
                <p className="text-center text-muted-foreground py-8">
                  Zatím žádná aktivita
                </p>
              )}
            </div>
          ) : (
            // All forms view with filtering
            <div className="space-y-4">
              {/* Filters and bulk actions */}
              <div className="space-y-4">
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Název firmy</label>
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                      <Input
                        placeholder="Hledat firmu..."
                        value={filters.company_name}
                        onChange={(e) => handleFilterChange('company_name', e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Obchodní zástupce</label>
                    <Input
                      placeholder="Hledat zástupce..."
                      value={filters.representative}
                      onChange={(e) => handleFilterChange('representative', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-input rounded-md"
                    >
                      <option value="">Všechny statusy</option>
                      <option value="draft">Rozpracováno</option>
                      <option value="submitted">Odesláno</option>
                      <option value="error">Chyba</option>
                    </select>
                  </div>
                </div>

                {/* Bulk actions */}
                {forms.length > 0 && (
                  <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedForms.size === forms.length && forms.length > 0}
                          onCheckedChange={handleSelectAll}
                          className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                        />
                        <span className="text-sm font-medium">
                          {selectedForms.size === forms.length ? 'Odznačit vše' : 'Označit vše'}
                        </span>
                      </div>
                      {selectedForms.size > 0 && (
                        <span className="text-sm text-amber-700">
                          Označeno {selectedForms.size} z {forms.length} formulářů
                        </span>
                      )}
                    </div>
                    {selectedForms.size > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleBulkDelete}
                        disabled={isDeleting}
                        className="flex items-center gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        {isDeleting ? 'Mažu...' : `Smazat (${selectedForms.size})`}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Forms list */}
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {forms.map((form) => (
                    <div key={form.uuid} className="border rounded-lg p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <Checkbox
                            checked={selectedForms.has(form.uuid)}
                            onCheckedChange={() => handleSelectForm(form.uuid)}
                            className="mt-1 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <Building2 className="h-4 w-4 text-slate-500 flex-shrink-0" />
                              <h3 className="font-medium text-slate-900 truncate">
                                {form.company_name}
                              </h3>
                              {getStatusBadge(form.status)}
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600">
                              {form.contact_person && (
                                <div className="flex items-center gap-2">
                                  <User className="h-3 w-3" />
                                  <span className="truncate">{form.contact_person}</span>
                                </div>
                              )}
                              
                              {form.email && (
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-400">✉</span>
                                  <span className="truncate">{form.email}</span>
                                </div>
                              )}
                              
                              {form.visit_date && (
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-3 w-3" />
                                  <span>
                                    {new Date(form.visit_date).toLocaleDateString('cs-CZ')}
                                  </span>
                                </div>
                              )}
                              
                              <div className="flex items-center gap-2">
                                <Clock className="h-3 w-3" />
                                <span>
                                  {formatDate(form.updated_at)}
                                </span>
                              </div>
                            </div>
                            
                            {form.representative && (
                              <div className="mt-2 text-sm text-slate-500">
                                Zástupce: {form.representative}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenForm(form.uuid)}
                            className="flex items-center gap-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <Edit3 className="h-4 w-4" />
                            Otevřít
                          </Button>
                          
                          {form.status === 'draft' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleContinueForm(form.uuid)}
                              className="flex items-center gap-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <ExternalLink className="h-4 w-4" />
                              Dokončit
                            </Button>
                          )}
                          
                          <div className="text-xs text-slate-500 text-center">
                            {form.completion_percentage}% dokončeno
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Zobrazeno {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} z {pagination.total}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={!pagination.hasPrev}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      Strana {pagination.page} z {pagination.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={!pagination.hasNext}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Health */}
      <Card>
        <CardHeader>
          <CardTitle>Zdraví systému</CardTitle>
          <CardDescription>
            Přehled klíčových komponent systému
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span>Raynet API</span>
              <div className="flex items-center gap-2">
                {isConnected ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-600">Online</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-red-600">Offline</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span>Formulář</span>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-600">Funkční</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span>Databáze formulářů</span>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-600">
                  {stats?.totalForms || 0} záznamů
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 